import { Component, ElementRef, ViewChild } from '@angular/core';
import { map, takeUntil } from 'rxjs';
import { CaseNote } from '../../../../models/ui/case-note.model';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { range } from 'lodash-es';
import { CaseNoteService } from '../../../../services/case-note.service';
import { NotificationManager } from '../../../../managers/notification.manager';
import { BaseComponent } from 'src/app/components/base.component';

@Component({
    selector: 'lex-case-notes-editor',
    templateUrl: './case-notes-editor.component.html',
    styleUrls: ['./case-notes-editor.component.scss'],
    standalone: false
})
export class CaseNotesEditorComponent extends BaseComponent {
	public caseNote: CaseNote;
	public caseNotesForm: UntypedFormGroup;
	public auditDocNotes: CaseNote[] = [];
	public pageNumberCollection: number[];
	public showHistory: boolean = false;

	@ViewChild('fileUpload', { static: false }) fileInputElement: ElementRef;

	constructor(private caseNoteService: CaseNoteService, private notificationManager: NotificationManager, private fb: UntypedFormBuilder) {
		super();
		this.caseNoteService.currentCaseNote$.pipe(takeUntil(this.destroy$)).subscribe((caseNote) => {
			this.caseNote = caseNote;
			if (caseNote) {
				this.pageNumberCollection = caseNote.pageCount > 0 ? range(1, caseNote.pageCount + 1) : [0];
				this.initializeForm(caseNote);
			}
		});

		this.caseNoteService.auditDocNote$.pipe(
			map(notes => this.auditDocNotes = notes),
			takeUntil(this.destroy$)
		).subscribe();
	}

	public save(): void {
		this.caseNoteService.saveCaseNote$(this.caseNote).pipe(
			takeUntil(this.destroy$)
		).subscribe((id) => this.caseNoteService.setCurrentCaseNote(id));
	}

	public cancel(): void {
		this.caseNoteService.refreshCaseNotes();
	}

	public openDocument(note: CaseNote): void {
		window.open(`/review/doc-viewer?DocId=${note.documentId}`, 'DocViewer');
	}

	public exportCaseNotes(): void {
		this.caseNoteService.exportCaseNotes();
	}

	public onImportFileSelected($event: Event): void {
		const file = ($event.target as HTMLInputElement).files[0];
		this.caseNoteService.importCaseNotes(file).subscribe({
			complete: () => {
				this.fileInputElement.nativeElement.value = '';
				this.notificationManager.displaySnackBarMessage(
					{
						message: 'Case notes imported successfully.',
						ttl: 3000,
						type: 'info'

					});
				this.caseNoteService.refreshCaseNotes();
			},
			error: () => {
				this.fileInputElement.nativeElement.value = '';
				this.notificationManager.displaySnackBarMessage(
					{
						message: 'An error occurred while attempting to import notes. Please check the file format and try again.',
						ttl: 5000,
						type: 'error'

					});
			}
		}
		);
	}

	public isSaveDisabled(): boolean {
		return this.caseNotesForm.invalid || this.caseNotesForm.pristine;
	}

	private initializeForm(note: CaseNote): void {
		this.caseNotesForm = this.fb.group({
			title: [note.title, [Validators.required]],
			description: [note.body],
			pageNumber: [note.pageNumber],
			shared: [!note.isPrivate]
		});

		this.caseNotesForm.valueChanges.pipe(
			takeUntil(this.destroy$)
		).subscribe((newValue) => {
			this.caseNote.title = newValue.title;
			this.caseNote.body = newValue.description;
			this.caseNote.isPrivate = !newValue.shared;
			this.caseNote.pageNumber = newValue.pageNumber;
		});
	}

}
